import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./CandidateInterviewPanel.css";
import DynamicMultipleSelectCheckmarks from "../../Components/Multiple-Select-Checkmarks/MultipleSelectCheckmarks";
import { DynamicContext } from "../../Context/DynamicContext.js";
import { get } from "../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  LIST_MEMEBERS_OF_TENANT,
} from "../../helper/url_helper";
import { TENANT_CORE_API_KEY } from "../../Tenant-Config/TenantConfig";
import NoDataSelectedIcon from "../../Assets/Images/IntPanelNoPanelIcon.svg";

function CandidateInterviewPanel({ selectedIds, onSelectionChange }) {
  const { userDetails } = useContext(DynamicContext);
  const isControlled = Array.isArray(selectedIds);
  const [internalSelected, setInternalSelected] = useState([]);
  const selectedRecruiters = isControlled ? selectedIds : internalSelected;
  const [recruiterOptions, setRecruiterOptions] = useState([]);
  const [isLoadingRecruiters, setIsLoadingRecruiters] = useState(false);
  const searchDebounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const selectedRecruitersRef = useRef(selectedRecruiters);
  selectedRecruitersRef.current = selectedRecruiters;

  const fetchRecruiters = useCallback(
    async (searchTerm = "") => {
      if (!userDetails?.tenant_id) {
        setRecruiterOptions([]);
        return;
      }

      const requestId = ++requestIdRef.current;
      const query = searchTerm.trim();
      const endpoint = query
        ? `${LIST_MEMEBERS_OF_TENANT}?tenant_id=${userDetails.tenant_id}&filter=${encodeURIComponent(query)}&offset=0&limit=30`
        : `${LIST_MEMEBERS_OF_TENANT}?tenant_id=${userDetails.tenant_id}&offset=0&limit=30`;

      try {
        setIsLoadingRecruiters(true);
        const response = await get(VYLABS_BASE_URL, endpoint, {
          headers: { "X-API-Key": TENANT_CORE_API_KEY },
        });

        if (requestId !== requestIdRef.current) return;

        const users = Array.isArray(response?.users) ? response.users : [];
        const options = users
          .filter((item) => item?.user || item?.id)
          .map((item) => {
            const user = item?.user || item;
            const fullName =
              [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
              user?.name ||
              "N/A";

            return {
              ...user,
              label: fullName,
              value: user?.id,
              email: user?.email || "",
            };
          })
          .filter((option) => option.value);

        setRecruiterOptions((currentOptions) => {
          const selectedOptions = currentOptions.filter((option) =>
            selectedRecruitersRef.current.some(
              (id) => String(id) === String(option.value),
            ),
          );
          const missingSelectedOptions = selectedOptions.filter(
            (selected) =>
              !options.some(
                (option) => String(option.value) === String(selected.value),
              ),
          );
          return [...missingSelectedOptions, ...options];
        });
      } catch (error) {
        console.error("Error fetching recruiters:", error);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoadingRecruiters(false);
        }
      }
    },
    [userDetails?.tenant_id],
  );

  const handleRecruiterSearch = useCallback(
    (searchTerm) => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      const query = (searchTerm || "").trim();
      if (!query) {
        fetchRecruiters("");
        return;
      }

      searchDebounceRef.current = setTimeout(() => {
        fetchRecruiters(query);
      }, 300);
    },
    [fetchRecruiters],
  );

  const handleRecruiterChange = useCallback((value) => {
    const ids = Array.isArray(value)
      ? value.filter(
          (id) => id !== undefined && id !== null && id !== "" && id !== "All",
        )
      : [];

    selectedRecruitersRef.current = ids;
    setInternalSelected(ids);
    if (typeof onSelectionChange === "function") {
      onSelectionChange(ids);
    }
  }, [onSelectionChange]);

  const selectedRecruiterDetails = useMemo(
    () =>
      selectedRecruiters
        .map((id) =>
          recruiterOptions.find(
            (recruiter) => String(recruiter.value) === String(id),
          ),
        )
        .filter(Boolean),
    [recruiterOptions, selectedRecruiters],
  );

  const multiselectSelectedRecruiters = useMemo(
    () =>
      selectedRecruiters.map(
        (id) =>
          recruiterOptions.find(
            (recruiter) => String(recruiter.value) === String(id),
          )?.value ?? id,
      ),
    [recruiterOptions, selectedRecruiters],
  );

  useEffect(() => {
    fetchRecruiters("");
  }, [fetchRecruiters]);

  useEffect(
    () => () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      requestIdRef.current += 1;
    },
    [],
  );

  return (
    <section className="candidate-interview-panel">
      <header className="candidate-interview-panel__header">
        <div className="candidate-interview-panel__title-row">
          <h2>Interview Panel</h2>
          {selectedRecruiterDetails.length > 0 && (
            <span className="candidate-interview-panel__count">
              <span aria-hidden="true">•</span>
              {selectedRecruiterDetails.length}{" "}
              {selectedRecruiterDetails.length === 1 ? "member" : "members"}
            </span>
          )}
        </div>
        <p>Assign internal team members to evaluate this candidate.</p>
      </header>

      <div className="candidate-interview-panel__search">
        <DynamicMultipleSelectCheckmarks
          options={recruiterOptions}
          label=""
          labelKey="label"
          valueKey="value"
          value={multiselectSelectedRecruiters}
          onChange={handleRecruiterChange}
          onSearchChange={handleRecruiterSearch}
          loading={isLoadingRecruiters}
          placeholder="Select team members"
          width="100%"
        />
      </div>

      <p className="candidate-interview-panel__helper">
        Only internal employees from your directory can be added. At least one
        is required.
      </p>

      {selectedRecruiterDetails.length === 0 ? (
        <div className="candidate-interview-panel__empty">
          <div className="candidate-interview-panel__empty-icon">
            <img src={NoDataSelectedIcon} alt="" aria-hidden="true" />
          </div>
          <div className="candidate-interview-panel__empty-copy">
            <h3>No panel members assigned</h3>
            <p>
              Search above to add interviewers like a Tech Lead or Senior
              Developer.
            </p>
          </div>
        </div>
      ) : (
        <div className="candidate-interview-panel__members">
          {selectedRecruiterDetails.map((recruiter) => {
            const initials = (recruiter.label || "N")
              .trim()
              .split(/\s+/)
              .slice(0, 2)
              .map((part) => part.charAt(0))
              .join("")
              .toUpperCase();

            return (
              <div
                className="candidate-interview-panel__member"
                key={recruiter.value}
              >
                <div className="candidate-interview-panel__avatar" aria-hidden="true">
                  {initials}
                </div>
                <div className="candidate-interview-panel__member-details">
                  <h3>{recruiter.label}</h3>
                  {recruiter.email && <p>{recruiter.email}</p>}
                </div>
                <button
                  type="button"
                  className="candidate-interview-panel__remove"
                  aria-label={`Remove ${recruiter.label}`}
                  onClick={() =>
                    handleRecruiterChange(
                      selectedRecruiters.filter(
                        (id) => String(id) !== String(recruiter.value),
                      ),
                    )
                  }
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="22"
                    height="22"
                    aria-hidden="true"
                  >
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default CandidateInterviewPanel;